import React, { RefObject } from "react";
import ReactMarkdown from "react-markdown";

type FieldProps = {
  label?: string;
  id: string;
  type: React.HTMLInputTypeAttribute;
  placeholder?: string;
  value?: string | number;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  overwriteOnly?: boolean;
  subtext?: string;
  onChangeHandler: (value: string) => void;
  onBlurHandler?: (value: string) => void;
  className?: string;
  pattern?: string;
  patternComment?: string;
  autoFocus?: boolean;
  ref?: RefObject<HTMLInputElement>;
};

const Field = React.forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      label,
      id,
      type,
      placeholder,
      value,
      onChangeHandler,
      onBlurHandler = () => {},
      required = false,
      disabled = false,
      readOnly = false,
      overwriteOnly = false,
      subtext,
      className = "",
      pattern,
      patternComment,
      autoFocus,
    },
    ref,
  ) => {
    const onChange: React.ChangeEventHandler<HTMLInputElement> = (event: React.ChangeEvent<HTMLInputElement>) =>
      onChangeHandler(event.target.value);

    // Trims leading and trailing whitespace when the user unfocuses the input
    const onBlur: React.FocusEventHandler<HTMLInputElement> = (event: React.FocusEvent<HTMLInputElement>) => {
      const trimmedValue = event.target.value.trim();
      onChangeHandler(trimmedValue);
      onBlurHandler(trimmedValue);
    };

    const patternInvalid = () => {
      return value?.toString() && value.toString().length > 0 && !new RegExp(`^${pattern}$`).test(value.toString());
    };

    return (
      <div className={className}>
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <input
          type={type}
          placeholder={placeholder}
          name={id}
          id={id}
          aria-describedby={subtext ? `${id}-description` : undefined}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          disabled={disabled}
          pattern={pattern}
          autoFocus={autoFocus}
          className={`mt-1 block w-full shadow-sm disabled:bg-gray-50 disabled:text-gray-600 focus:ring-primary-500 focus:border-primary-500 sm:text-sm border-gray-300 rounded-md ${
            overwriteOnly
              ? "placeholder:text-current focus:placeholder:placeholder-transparent"
              : "placeholder:text-gray-300"
          } `}
          ref={ref}
          readOnly={readOnly}
        />

        {pattern && patternInvalid() && (
          <p className="mt-1 text-xs font-medium text-red-600">{patternComment ?? "Input format invalid."}</p>
        )}
        {subtext && (
          <div className="mt-2 text-xs text-gray-400 whitespace-pre-line" id={`${id}-description`}>
            <ReactMarkdown
              components={{
                a: ({ href, children, ...props }) => (
                  <a className="text-primary-500" href={href} target="_blank" rel="noopener noreferrer" {...props}>
                    {children}
                  </a>
                ),
              }}
            >
              {subtext}
            </ReactMarkdown>
          </div>
        )}
      </div>
    );
  },
);

Field.displayName = "Field";

export default Field;
export const PasswordDot = "•";
